import {
    getAddress,
    hashTypedData,
    hexToBytes,
    keccak256,
    numberToBytes,
    stringToHex,
    stringToBytes,
} from 'viem';
import type { Address, Hex } from 'viem';

import {
    DEFAULT_ANCHOR_CHAIN_ID,
    type AnchorChainId,
} from '@/features/wallet/config/anchors';
import {
    tradingSessionStatementSchema,
    tradingSessionTypedDataSchema,
} from '@/lib/api/tradingSession/schemas';
import type {
    TradingSessionStatement,
    TradingSessionTypedData,
} from '@/lib/api/tradingSession/types';
import { custodialManagerFor } from '@/lib/config/custodialManagers';

import {
    base64UrlToBytes,
    bytesToBase64Url,
    sha256Base64Url,
} from './webauthn';

const CANONICAL_SESSION_PREFIX = stringToBytes('snappy-trading-session-v1');
const PASSKEY_DISCOVERY_PREFIX = stringToBytes('snappy-passkey-discovery-v1:');

/**
 * The trading-session EIP-712 domain for one authorization anchor: the anchor's
 * chain id, and the manager that anchor's statements are scoped to. Anchor 25
 * reproduces the domain this app has always used, byte for byte, so existing
 * Cronos signatures and golden vectors are unchanged.
 */
export function tradingSessionDomain(
    anchorChainId: AnchorChainId = DEFAULT_ANCHOR_CHAIN_ID,
) {
    return {
        name: 'SnappyTradingSession',
        version: '1',
        chainId: anchorChainId,
        verifyingContract: custodialManagerFor(anchorChainId),
    } as const;
}

export const SNAPPY_TRADING_SESSION_TYPES = {
    TradingSession: [
        { name: 'sessionId', type: 'bytes32' },
        { name: 'owner', type: 'address' },
        { name: 'authorizationWallet', type: 'address' },
        { name: 'tradingWallet', type: 'address' },
        { name: 'audienceHash', type: 'bytes32' },
        { name: 'originHash', type: 'bytes32' },
        { name: 'issuedAt', type: 'uint64' },
        { name: 'expiresAt', type: 'uint64' },
    ],
} as const;

/** Same anchor scoping as {@link tradingSessionDomain}, for passkey management. */
export function passkeyManagementDomain(
    anchorChainId: AnchorChainId = DEFAULT_ANCHOR_CHAIN_ID,
) {
    return {
        name: 'SnappyPasskeyManagement',
        version: '1',
        chainId: anchorChainId,
        verifyingContract: custodialManagerFor(anchorChainId),
    } as const;
}

export const PASSKEY_REGISTRATION_TYPES = {
    PasskeyRegistration: [
        { name: 'owner', type: 'address' },
        { name: 'challengeHash', type: 'bytes32' },
    ],
} as const;

export type PasskeyRegistrationTypedData = ReturnType<
    typeof createPasskeyRegistrationTypedData
>;

function concatBytes(parts: readonly Uint8Array[]): Uint8Array {
    const result = new Uint8Array(
        parts.reduce((length, part) => length + part.length, 0),
    );
    let offset = 0;
    for (const part of parts) {
        result.set(part, offset);
        offset += part.length;
    }
    return result;
}

export function buildCanonicalTradingSessionBytes(
    input: TradingSessionStatement,
): Uint8Array {
    const statement = tradingSessionStatementSchema.parse(input);
    const sessionId = hexToBytes(statement.sessionId as Hex);
    if (sessionId.every((byte) => byte === 0)) {
        throw new Error('Trading-session ID cannot be zero.');
    }

    const addresses = [
        statement.owner,
        statement.authorizationWallet,
        statement.tradingWallet,
    ].map((address) => {
        const bytes = hexToBytes(getAddress(address));
        if (bytes.every((byte) => byte === 0)) {
            throw new Error('Trading-session addresses cannot be zero.');
        }
        return bytes;
    });

    return concatBytes([
        CANONICAL_SESSION_PREFIX,
        sessionId,
        ...addresses,
        hexToBytes(keccak256(stringToHex(statement.audience))),
        hexToBytes(keccak256(stringToHex(statement.origin))),
        numberToBytes(BigInt(statement.issuedAt), { size: 8 }),
        numberToBytes(BigInt(statement.expiresAt), { size: 8 }),
    ]);
}

export async function computeCanonicalTradingSessionChallenge(
    statement: TradingSessionStatement,
): Promise<string> {
    return sha256Base64Url(buildCanonicalTradingSessionBytes(statement));
}

export async function assertCanonicalTradingSessionChallenge(
    statement: TradingSessionStatement,
    challenge: string,
): Promise<void> {
    const expected = await computeCanonicalTradingSessionChallenge(statement);
    if (challenge !== expected) {
        throw new Error(
            'The server returned a non-canonical passkey challenge; refusing to open WebAuthn.',
        );
    }
}

export function assertPasskeyDiscoveryChallenge(challenge: string): void {
    let decoded: Uint8Array;
    try {
        decoded = base64UrlToBytes(challenge);
    } catch {
        throw new Error(
            'The server returned an invalid passkey discovery challenge.',
        );
    }
    if (
        bytesToBase64Url(decoded) !== challenge ||
        decoded.length !== PASSKEY_DISCOVERY_PREFIX.length + 32 ||
        !PASSKEY_DISCOVERY_PREFIX.every(
            (byte, index) => decoded[index] === byte,
        )
    ) {
        throw new Error(
            'The server returned an invalid passkey discovery challenge.',
        );
    }
}

export function createTradingSessionTypedData(
    statement: TradingSessionStatement,
    anchorChainId: AnchorChainId = DEFAULT_ANCHOR_CHAIN_ID,
): TradingSessionTypedData {
    return tradingSessionTypedDataSchema.parse({
        domain: tradingSessionDomain(anchorChainId),
        types: SNAPPY_TRADING_SESSION_TYPES,
        primaryType: 'TradingSession',
        message: {
            sessionId: statement.sessionId,
            owner: statement.owner,
            authorizationWallet: statement.authorizationWallet,
            tradingWallet: statement.tradingWallet,
            audienceHash: keccak256(stringToHex(statement.audience)),
            originHash: keccak256(stringToHex(statement.origin)),
            issuedAt: statement.issuedAt,
            expiresAt: statement.expiresAt,
        },
    });
}

export function hashTradingSession(
    statement: TradingSessionStatement,
    anchorChainId: AnchorChainId = DEFAULT_ANCHOR_CHAIN_ID,
): Hex {
    const typedData = createTradingSessionTypedData(statement, anchorChainId);
    return hashTypedData({
        ...typedData,
        domain: {
            ...typedData.domain,
            verifyingContract: getAddress(typedData.domain.verifyingContract),
        },
        message: {
            ...typedData.message,
            sessionId: typedData.message.sessionId as Hex,
            owner: getAddress(typedData.message.owner),
            authorizationWallet: getAddress(
                typedData.message.authorizationWallet,
            ),
            tradingWallet: getAddress(typedData.message.tradingWallet),
            audienceHash: typedData.message.audienceHash as Hex,
            originHash: typedData.message.originHash as Hex,
            issuedAt: BigInt(typedData.message.issuedAt),
            expiresAt: BigInt(typedData.message.expiresAt),
        },
    });
}

export function createPasskeyRegistrationTypedData(
    ownerInput: Address,
    challengeId: string,
    anchorChainId: AnchorChainId = DEFAULT_ANCHOR_CHAIN_ID,
) {
    const owner = getAddress(ownerInput);
    return {
        domain: passkeyManagementDomain(anchorChainId),
        types: PASSKEY_REGISTRATION_TYPES,
        primaryType: 'PasskeyRegistration',
        message: {
            owner,
            challengeHash: keccak256(stringToHex(challengeId)),
        },
    } as const;
}

export function hashPasskeyRegistration(
    owner: Address,
    challengeId: string,
    anchorChainId: AnchorChainId = DEFAULT_ANCHOR_CHAIN_ID,
): Hex {
    return hashTypedData(
        createPasskeyRegistrationTypedData(owner, challengeId, anchorChainId),
    );
}

export function assertTradingSessionSigningPayload(
    typedData: unknown,
    statement: TradingSessionStatement,
    expectedOwnerInput: string,
    anchorChainId: AnchorChainId = DEFAULT_ANCHOR_CHAIN_ID,
): asserts typedData is TradingSessionTypedData {
    let statementOwner: Address;
    let expectedOwner: Address;
    try {
        statementOwner = getAddress(statement.owner);
        expectedOwner = getAddress(expectedOwnerInput);
        getAddress(statement.authorizationWallet);
        getAddress(statement.tradingWallet);
    } catch {
        throw new Error('Trading authority contains an invalid address.');
    }
    if (statementOwner.toLowerCase() !== expectedOwner.toLowerCase()) {
        throw new Error(
            'Trading authority owner does not match the connected wallet.',
        );
    }

    const parsed = tradingSessionTypedDataSchema.safeParse(typedData);
    if (
        !parsed.success ||
        !equalStructuredValues(
            parsed.data,
            createTradingSessionTypedData(statement, anchorChainId),
        )
    ) {
        throw new Error(
            'The server returned mismatched typed data; refusing to sign.',
        );
    }
}

function canonicalize(value: unknown): unknown {
    if (Array.isArray(value)) return value.map(canonicalize);
    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value)
                .sort(([left], [right]) => left.localeCompare(right))
                .map(([key, entry]) => [key, canonicalize(entry)]),
        );
    }
    return value;
}

function equalStructuredValues(left: unknown, right: unknown): boolean {
    return (
        JSON.stringify(canonicalize(left)) ===
        JSON.stringify(canonicalize(right))
    );
}
